import React, { useState, useEffect } from 'react';
import { MdDownloadForOffline } from 'react-icons/md';
import { Link, useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { motion } from 'framer-motion';

import { client, urlFor } from '../client';
import MasonryLayout from './MasonryLayout';
import { pinDetailMorePinQuery, pinDetailQuery } from '../utils/data';
import Spinner from './Spinner';

const PinDetail = ({ user }) => {
  const { pinId } = useParams();
  const [pins, setPins] = useState(null);
  const [pinDetail, setPinDetail] = useState(null);
  const [comment, setComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);
  const [likingPost, setLikingPost] = useState(false);

  const fetchPinDetails = () => {
    const query = pinDetailQuery(pinId);

    if (query) {
      client.fetch(query).then((data) => {
        setPinDetail(data[0]);
        if (data[0]) {
          const morePinsQuery = pinDetailMorePinQuery(data[0]);
          client.fetch(morePinsQuery).then(setPins);
        }
      });
    }
  };

  useEffect(() => {
    fetchPinDetails();
    // eslint-disable-next-line
  }, [pinId]);

  const addComment = () => {
    if (!comment) return;

    setAddingComment(true);

    client
      .patch(pinId)
      .setIfMissing({ comments: [] })
      .insert('after', 'comments[-1]', [
        {
          comment,
          _key: uuidv4(),
          postedBy: {
            _type: 'reference',
            _ref: user?.user?._id,
          },
        },
      ])
      .commit()
      .then(() => {
        setComment('');
        fetchPinDetails();
        setAddingComment(false);
      });
  };

  const toggleLike = () => {
    if (!user) {
      alert('Please login to like posts!');
      return;
    }

    setLikingPost(true);

    const userId = user?.user?._id;
    const userLiked = pinDetail.likes?.some((like) => like._ref === userId);

    let updatedLikes;

    if (userLiked) {
      updatedLikes = pinDetail.likes.filter((like) => like._ref !== userId);
      setPinDetail({ ...pinDetail, likes: updatedLikes });

      client
        .patch(pinId)
        .set({ likes: updatedLikes })
        .commit()
        .then(() => {
          setLikingPost(false);
          fetchPinDetails();
        });
    } else {
      const newLike = { _type: 'reference', _ref: userId };
      updatedLikes = [...(pinDetail.likes || []), newLike];
      setPinDetail({ ...pinDetail, likes: updatedLikes });

      client
        .patch(pinId)
        .setIfMissing({ likes: [] })
        .insert('after', 'likes[-1]', [newLike])
        .commit()
        .then(() => {
          setLikingPost(false);
          fetchPinDetails();
        });
    }
  };

  if (!pinDetail) return <Spinner message="Loading post..." />;

  const userId = user?.user?._id;
  const userLiked = pinDetail.likes?.some((like) => like._ref === userId);

  return (
    <>
      <div
        className="flex flex-col xl:flex-row m-auto bg-gradient-to-br from-gray-100 via-blue-100 to-purple-100 dark:from-gray-900 dark:via-slate-900 dark:to-blue-900 dark:text-white transition-colors duration-300 rounded-3xl shadow-2xl border border-blue-400/20 max-w-6xl"
        style={{ maxWidth: '1500px', marginTop: '2rem', marginBottom: '2rem' }}
      >
        {/* Image Section */}
        <div className="flex justify-center items-center md:items-start flex-initial bg-white/80 dark:bg-gray-900/80 rounded-t-3xl xl:rounded-l-3xl xl:rounded-tr-none p-4">
          <img
            className="rounded-2xl object-cover max-h-[600px] shadow-lg border border-gray-200 dark:border-gray-700"
            src={urlFor(pinDetail?.image).url()}
            alt="user-post"
            style={{ maxWidth: '100%', width: 'auto' }}
          />
        </div>

        {/* Details Section */}
        <div className="w-full p-8 flex-1 xl:min-w-620 flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <a
              href={`${pinDetail.image.asset.url}?dl=`}
              download
              className="bg-white dark:bg-slate-700 p-2 text-xl rounded-full flex items-center justify-center text-gray-700 dark:text-white opacity-80 hover:opacity-100 shadow-md transition"
              title="Download"
            >
              <MdDownloadForOffline />
            </a>
            {pinDetail.destination && (
              <a
                href={pinDetail.destination}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 dark:text-blue-400 underline font-semibold hover:text-blue-800 dark:hover:text-blue-300 transition"
              >
                Visit Link
              </a>
            )}
          </div>

          <h1 className="text-4xl font-extrabold mt-2 mb-2 break-words text-gray-900 dark:text-white drop-shadow">
            {pinDetail.title}
          </h1>
          <p className="mb-4 text-lg text-gray-700 dark:text-gray-200">{pinDetail.about}</p>

          <Link
            to={`/user-profile/${pinDetail?.postedBy?._id}`}
            className="flex gap-3 mt-2 items-center hover:bg-blue-100/40 dark:hover:bg-blue-900/40 rounded-xl p-2 transition"
          >
            <img
              src={pinDetail?.postedBy?.image}
              className="w-12 h-12 rounded-full border-2 border-blue-400 shadow"
              alt="user-profile"
            />
            <p className="font-bold text-lg">{pinDetail?.postedBy?.userName}</p>
          </Link>

          <motion.button
            whileTap={{ scale: 1.1 }}
            onClick={toggleLike}
            disabled={likingPost}
            className={`mt-6 flex items-center gap-2 px-6 py-2 rounded-full font-semibold text-lg shadow-md transition
              ${userLiked ? 'bg-red-500 text-white' : 'bg-gray-300 text-gray-900 dark:bg-gray-700 dark:text-white hover:bg-gray-400 dark:hover:bg-gray-600'}
              ${likingPost ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
            title={userLiked ? 'Unlike' : 'Like'}
          >
            {userLiked ? '♥ Liked' : '♡ Like'} ({pinDetail.likes?.length || 0})
          </motion.button>

          <h2 className="mt-8 text-2xl font-bold text-gray-900 dark:text-white">Comments</h2>
          <div className="max-h-80 overflow-y-auto mt-2 pr-2 hide-scrollbar">
            {pinDetail?.comments?.length > 0 ? pinDetail.comments.map((comment, i) => (
              <div
                key={comment._key || i}
                className="flex gap-3 mt-4 items-start bg-white/70 dark:bg-slate-800 p-3 rounded-xl shadow"
              >
                <img
                  src={comment?.postedBy?.image || '/default-profile.png'}
                  className="w-10 h-10 rounded-full border border-gray-300 dark:border-gray-700"
                  alt="comment-user"
                />
                <div>
                  <p className="font-bold text-blue-700 dark:text-blue-300">{comment?.postedBy?.userName || 'Unknown'}</p>
                  <p className="text-gray-800 dark:text-gray-200">{comment.comment}</p>
                </div>
              </div>
            )) : (
              <p className="text-gray-500 dark:text-gray-400 mt-4">No comments yet. Be the first to comment!</p>
            )}
          </div>

          <div className="flex flex-wrap mt-8 gap-3 items-center">
            <Link to={`/user-profile/${userId}`}>
              <img
                src={user?.user?.image || '/default-profile.png'}
                alt="user-profile"
                className="w-10 h-10 rounded-full border border-blue-300"
              />
            </Link>
            <input
              type="text"
              className="flex-1 border-gray-200 dark:border-gray-700 bg-white/90 dark:bg-slate-800 outline-none border-2 p-2 rounded-2xl focus:border-blue-400 dark:focus:border-blue-500 transition-colors text-gray-900 dark:text-white"
              placeholder="Add a comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            <button
              onClick={addComment}
              className="bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-full px-6 py-2 font-semibold hover:from-blue-600 hover:to-purple-600 transition disabled:opacity-50"
              disabled={addingComment}
            >
              {addingComment ? 'Posting...' : 'Post'}
            </button>
          </div>
        </div>
      </div>

      {pins?.length > 0 ? (
        <>
          <h2 className="text-center font-bold text-2xl mt-8 mb-4 dark:text-white dark:bg-slate-900">More like this</h2>
          <div className="px-2 pb-8">
            <MasonryLayout pins={pins} />
          </div>
        </>
      ) : (
        <Spinner message="Loading more posts..." />
      )}
    </>
  );
};

export default PinDetail;